import React, { ReactElement, useMemo, useState } from 'react';
import AuthorizationTokenStorage from '../services/auth/AuthorizationTokenStorage';
import { actionCreators } from '../store/actions';
import ReduxActions from '../store/ReduxActions';
import AuthorizationManager, { LoginData } from '../services/auth/AuthorizationManager';
import { AxiosRequestConfig } from 'axios';
import { LoginResponseInterface } from '../services/exports/Interfaces';
import collect from 'collect.js';
import { Response } from '../services/exports/Types';

interface Props {
  children: ReactElement;
}

interface AuthContextInterface {
  isLoggedIn: boolean;
  isGuest: boolean;
  login: (data: LoginData, config?: AxiosRequestConfig) => Promise<Response<LoginResponseInterface>>;
  logout: () => void;
  onLogin: (data: LoginResponseInterface, resetOrder?: boolean) => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextInterface>(null);

export default function AuthProvider({ children }: Props) {
  const [isLoggedIn, setIsLoggedIn] = useState(!!AuthorizationTokenStorage.getToken());

  const login = async (data: LoginData, config?: AxiosRequestConfig) => {
    const result = await AuthorizationManager.postLogin(data, config);

    if (result.success) {
      await onLogin(result.response.data);
    }

    return result;
  };

  const logout = async () => {
    AuthorizationTokenStorage.deleteToken();

    onLogout();
  };

  async function onLogin(data: LoginResponseInterface, resetOrder: boolean = true): Promise<void> {
    await AuthorizationTokenStorage.saveToken(data.auth_token);

    setIsLoggedIn(true);

    ReduxActions.batchActions(
      collect([actionCreators.profile.setProfile(data.user)])
        .when(resetOrder, (collection) =>
          collection.push(
            actionCreators.order.updateOrder({
              ...data.user,
              customer_phone_number: data.user.phone_number,
              customer_first_name: data.user.first_name,
              customer_last_name: data.user.last_name,
              customer_email: data.user.email,
            }),
          ),
        )
        .toArray(),
    );
  }

  function onLogout(): void {
    setIsLoggedIn(false);

    ReduxActions.batchActions([
      actionCreators.order.resetOrder(),
      actionCreators.profile.setProfile(null),
      actionCreators.initialData.reset(),
      actionCreators.paymentOptions.reset(),
    ]);
  }

  const context = useMemo(
    () => ({
      isLoggedIn,
      isGuest: !isLoggedIn,
      login,
      logout,
      onLogin,
    }),
    [isLoggedIn],
  );

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>;
}
